/**
 * Admin Dashboard Page Component
 * Admin-only page displaying dashboard overview and quick links
 * Shows statistics, recent activity, and admin shortcuts
 *
 * @module pages/AdminPage
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { logger } from '@/utils/logger'
import { Layout, AdminRoute } from '@/components/layout'
import { Button } from '@/components/common'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/utils/constants'

/**
 * Dashboard statistics data structure
 */
interface DashboardStats {
  totalUsers: number
  totalPhotos: number
  totalAlbums: number
  pendingReviews: number
}

/**
 * Admin page component
 * Displays admin dashboard with statistics and quick links
 * Admin-only route - requires admin role
 */
export default function AdminPage() {
  const auth = useAuth()
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalPhotos: 0,
    totalAlbums: 0,
    pendingReviews: 0,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>('')

  logger.debug('[WALRUS] AdminPage: rendering admin page', {
    userId: auth.user?.id,
    userRole: auth.user?.role,
  })

  /**
   * Load dashboard statistics
   */
  useEffect(() => {
    const loadStats = async () => {
      logger.debug('[WALRUS] AdminPage: loading dashboard statistics')
      setIsLoading(true)
      setError('')

      try {
        // TODO: Fetch stats from admin API endpoints
        logger.debug('[WALRUS] AdminPage: stats loaded (placeholder)')
        setStats({
          totalUsers: 0,
          totalPhotos: 0,
          totalAlbums: 0,
          pendingReviews: 0,
        })
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load statistics'
        logger.error('[WALRUS] AdminPage: error loading stats', { error: errorMessage })
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    loadStats()
  }, [])

  return (
    <AdminRoute>
      <Layout contentClassName="bg-walrus-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
          {/* Header */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h1 className="text-4xl sm:text-5xl font-bold text-walrus-black">
                Admin Dashboard
              </h1>
              <span className="bg-walrus-black text-walrus-white px-3 py-1 rounded-full text-sm font-semibold">
                Admin
              </span>
            </div>
            <p className="text-walrus-silver text-lg">
              Overview of family members, photos, albums, and system activity
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg">
              <p className="font-semibold">Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Quick Stats Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Users Stat */}
            <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-walrus-silver text-sm font-semibold uppercase tracking-wide">
                    Family Members
                  </h3>
                  <span className="text-2xl">👥</span>
                </div>
                {isLoading ? (
                  <div className="h-8 bg-walrus-gray rounded animate-pulse"></div>
                ) : (
                  <p className="text-4xl font-bold text-walrus-black">
                    {stats.totalUsers}
                  </p>
                )}
              </div>
            </div>

            {/* Total Photos Stat */}
            <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-walrus-silver text-sm font-semibold uppercase tracking-wide">
                    Total Photos
                  </h3>
                  <span className="text-2xl">📸</span>
                </div>
                {isLoading ? (
                  <div className="h-8 bg-walrus-gray rounded animate-pulse"></div>
                ) : (
                  <p className="text-4xl font-bold text-walrus-black">
                    {stats.totalPhotos}
                  </p>
                )}
              </div>
            </div>

            {/* Total Albums Stat */}
            <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-walrus-silver text-sm font-semibold uppercase tracking-wide">
                    Total Albums
                  </h3>
                  <span className="text-2xl">📁</span>
                </div>
                {isLoading ? (
                  <div className="h-8 bg-walrus-gray rounded animate-pulse"></div>
                ) : (
                  <p className="text-4xl font-bold text-walrus-black">
                    {stats.totalAlbums}
                  </p>
                )}
              </div>
            </div>

            {/* Pending Reviews Stat */}
            <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-walrus-silver text-sm font-semibold uppercase tracking-wide">
                    Pending Reviews
                  </h3>
                  <span className="text-2xl">⏳</span>
                </div>
                {isLoading ? (
                  <div className="h-8 bg-walrus-gray rounded animate-pulse"></div>
                ) : (
                  <p className="text-4xl font-bold text-walrus-black">
                    {stats.pendingReviews}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
            <h2 className="text-2xl font-bold text-walrus-black mb-8">
              Quick Actions
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to={ROUTES.ADMIN_USERS} className="block">
                <Button
                  variant="secondary"
                  size="lg"
                  className="w-full text-left justify-start"
                >
                  <span>👥</span>
                  Manage Family
                </Button>
              </Link>

              <Link to={ROUTES.ADMIN_PHOTOS} className="block">
                <Button
                  variant="secondary"
                  size="lg"
                  className="w-full text-left justify-start"
                >
                  <span>📸</span>
                  Manage Photos
                </Button>
              </Link>

              <button
                className="block px-4 py-3 bg-walrus-cream text-walrus-black font-semibold border border-walrus-gray rounded-lg hover:bg-walrus-white transition-colors duration-200 text-left flex items-center gap-3 disabled opacity-50 cursor-not-allowed"
                disabled
                title="Coming soon"
              >
                <span>⚙️</span>
                System Settings
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
            <h2 className="text-2xl font-bold text-walrus-black mb-8">
              Recent Activity
            </h2>

            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-12 bg-walrus-white border border-walrus-gray rounded animate-pulse"
                  ></div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-walrus-silver text-lg mb-4">
                  No recent activity to display
                </p>
                <p className="text-walrus-gray text-sm">
                  Activity logs will appear here as family members interact with the platform
                </p>
              </div>
            )}
          </div>

          {/* System Health */}
          <div className="bg-walrus-cream border border-walrus-gray rounded-xl p-8">
            <h2 className="text-2xl font-bold text-walrus-black mb-8">
              System Status
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-walrus-white border border-walrus-gray rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="text-walrus-black">Database Connection</span>
                </div>
                <span className="text-walrus-silver text-sm">Healthy</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-walrus-white border border-walrus-gray rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="text-walrus-black">S3 Storage</span>
                </div>
                <span className="text-walrus-silver text-sm">Healthy</span>
              </div>

              <div className="flex items-center justify-between p-4 bg-walrus-white border border-walrus-gray rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="text-walrus-black">Authentication Service</span>
                </div>
                <span className="text-walrus-silver text-sm">Healthy</span>
              </div>
            </div>
          </div>
        </div>

        {/* TODO: Add real-time metrics from backend */}
        {/* TODO: Add user activity timeline */}
        {/* TODO: Add system performance monitoring */}
        {/* TODO: Add error/exception tracking */}
        {/* TODO: Add database storage usage display */}
        {/* TODO: Add scheduled task management */}
        {/* TODO: Add backup/restore functionality */}
      </Layout>
    </AdminRoute>
  )
}
